import logger from "../utils/logger";
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { FileText, Upload, Download, Trash2, File, Image, FileSpreadsheet } from 'lucide-react';

import API_URL from '../utils/api';

const DOC_TYPES = [
    { value: 'id_proof', label: 'ID Proof' },
    { value: 'address_proof', label: 'Address Proof' },
    { value: 'contract', label: 'Employment Contract' },
    { value: 'certificate', label: 'Certificate' },
    { value: 'resume', label: 'Resume / CV' },
    { value: 'tax', label: 'Tax Document' },
    { value: 'other', label: 'Other' },
];

const getFileIcon = (contentType) => {
    if (!contentType) return <File className="h-5 w-5 text-slate-500" />;
    if (contentType.startsWith('image/')) return <Image className="h-5 w-5 text-purple-600" />;
    if (contentType.includes('pdf')) return <FileText className="h-5 w-5 text-red-600" />;
    if (contentType.includes('spreadsheet') || contentType.includes('excel') || contentType.includes('csv')) return <FileSpreadsheet className="h-5 w-5 text-green-600" />;
    return <File className="h-5 w-5 text-brand" />;
};

const formatSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const EmployeeDocuments = ({ employeeId, employeeName, canManage }) => {
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [docType, setDocType] = useState('other');
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (employeeId) fetchDocuments();
    }, [employeeId]);

    const fetchDocuments = async () => {
        try {
            const { data } = await axios.get(`${API_URL}/api/employees/${employeeId}/documents`, { withCredentials: true });
            setDocuments(data);
        } catch (error) {
            logger.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        const formData = new FormData();
        formData.append('file', file);

        try {
            await axios.post(`${API_URL}/api/employees/${employeeId}/documents?doc_type=${docType}`, formData, {
                withCredentials: true,
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            fetchDocuments();
        } catch (error) {
            alert(error.response?.data?.detail || 'Upload failed');
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleDownload = async (docId, filename) => {
        try {
            const response = await axios.get(`${API_URL}/api/documents/${docId}/download`, {
                withCredentials: true,
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            alert('Download failed');
        }
    };

    const handleDelete = async (docId) => {
        if (!window.confirm('Delete this document?')) return;
        try {
            await axios.delete(`${API_URL}/api/documents/${docId}`, { withCredentials: true });
            fetchDocuments();
        } catch (error) {
            alert('Delete failed');
        }
    };

    const handleVerify = async (docId, action) => {
        let payload = { action };
        if (action === 'reject') {
            const reason = window.prompt('Reason for rejection (e.g., "Image blurry — please re-upload"):');
            if (!reason) return;
            payload.rejection_reason = reason;
        }
        try {
            await axios.put(`${API_URL}/api/documents/${docId}/verify`, payload, { withCredentials: true });
            fetchDocuments();
        } catch (error) {
            alert(error.response?.data?.detail || 'Verification failed');
        }
    };

    const VerifyBadge = ({ doc }) => {
        const status = doc.verification_status || 'pending';
        if (status === 'approved') return (
            <Badge className="rounded-md bg-emerald-100 text-emerald-700 text-[10px] font-bold flex items-center gap-1">
                ✓ Verified
            </Badge>
        );
        if (status === 'rejected') return (
            <Badge className="rounded-md bg-rose-100 text-rose-700 text-[10px] font-bold" title={doc.rejection_reason || ''}>
                ✕ Rejected
            </Badge>
        );
        return (
            <Badge className="rounded-md bg-amber-100 text-amber-700 text-[10px] font-bold">
                ⌛ Pending HR
            </Badge>
        );
    };

    if (loading) return <div className="text-sm text-slate-500 py-4">Loading documents...</div>;

    return (
        <Card className="rounded-none border-slate-200" data-testid="employee-documents">
            <CardHeader className="border-b border-slate-200">
                <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        <FileText className="h-5 w-5 text-brand" />
                        Documents {employeeName && <span className="text-slate-400 font-normal text-sm">- {employeeName}</span>}
                    </CardTitle>
                    {canManage && (
                        <div className="flex items-center gap-2">
                            <Select value={docType} onValueChange={setDocType}>
                                <SelectTrigger className="w-[150px] rounded-none text-sm" data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                                <SelectContent className="rounded-none">
                                    {DOC_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            <Button
                                size="sm"
                                className="bg-brand hover:bg-brand rounded-none"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploading}
                                data-testid="upload-document-btn"
                            >
                                <Upload className="h-4 w-4 mr-1" />
                                {uploading ? 'Uploading...' : 'Upload'}
                            </Button>
                            <input ref={fileInputRef} type="file" className="hidden" onChange={handleUpload} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt,.csv,.xlsx" />
                        </div>
                    )}
                </div>
            </CardHeader>
            <CardContent className="p-0">
                {documents.length === 0 ? (
                    <div className="py-8 text-center text-slate-500">No documents uploaded yet</div>
                ) : (
                    <div className="divide-y divide-slate-100">
                        {documents.map((doc) => (
                            <div key={doc._id} className="flex items-center gap-4 px-4 py-3 hover:bg-slate-50" data-testid={`document-${doc._id}`}>
                                <div className="flex-shrink-0">{getFileIcon(doc.content_type)}</div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-slate-900 truncate">{doc.original_filename}</p>
                                    <div className="flex gap-2 items-center text-xs text-slate-500 mt-0.5 flex-wrap">
                                        <Badge className="rounded-md bg-slate-100 text-slate-600 text-[10px]">{DOC_TYPES.find(t => t.value === doc.doc_type)?.label || doc.doc_type}</Badge>
                                        <VerifyBadge doc={doc} />
                                        <span>{formatSize(doc.size)}</span>
                                        <span>{new Date(doc.created_at).toLocaleDateString()}</span>
                                        {doc.verified_by && doc.verification_status === 'approved' && (
                                            <span className="text-emerald-600">by {doc.verified_by}</span>
                                        )}
                                    </div>
                                    {doc.verification_status === 'rejected' && doc.rejection_reason && (
                                        <p className="text-[11px] text-rose-600 mt-1">⚠ {doc.rejection_reason}</p>
                                    )}
                                </div>
                                <div className="flex gap-1 flex-shrink-0">
                                    {canManage && (doc.verification_status || 'pending') === 'pending' && (
                                        <>
                                            <Button size="sm" variant="ghost" className="rounded-md h-8 px-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 text-xs font-bold" onClick={() => handleVerify(doc._id, 'approve')} data-testid={`verify-approve-${doc._id}`} title="Approve">
                                                ✓
                                            </Button>
                                            <Button size="sm" variant="ghost" className="rounded-md h-8 px-2 text-rose-600 hover:text-rose-700 hover:bg-rose-50 text-xs font-bold" onClick={() => handleVerify(doc._id, 'reject')} data-testid={`verify-reject-${doc._id}`} title="Reject">
                                                ✕
                                            </Button>
                                        </>
                                    )}
                                    <Button size="sm" variant="ghost" className="rounded-md h-8 w-8 p-0" onClick={() => handleDownload(doc._id, doc.original_filename)} data-testid={`download-doc-${doc._id}`}>
                                        <Download className="h-4 w-4" />
                                    </Button>
                                    {canManage && (
                                        <Button size="sm" variant="ghost" className="rounded-md h-8 w-8 p-0 text-red-600 hover:text-red-700" onClick={() => handleDelete(doc._id)} data-testid={`delete-doc-${doc._id}`}>
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default EmployeeDocuments;
